import { useRef, type PointerEvent, type RefObject } from "react";

/** How far a finger has to travel on the handle before letting go counts as a drag, in px. */
const DRAG_PX = 32;

/** Where a two-stop sheet stands: the card, or the full height. There is no third. */
export type PoiSnap = "card" | "full";

/**
 * The handle of a two-stop sheet, shared by the places sheet and the place view: a tap toggles
 * between the card and the full stop, a drag up goes to the full stop, a drag down back to the
 * card, and a drag down from the card leaves it the card. While the finger is down the sheet's
 * height follows it; the stylesheet's max-height still caps that, and letting go puts it back.
 * Spread what it returns onto the handle button.
 */
export function useSnapHandle({
  sheet,
  open,
  onSnap,
}: {
  /** The sheet the handle belongs to: the element whose height follows the finger. */
  sheet: RefObject<HTMLElement | null>;
  /** True at the full stop. */
  open: boolean;
  /** Told of every move between the stops. */
  onSnap: (snap: PoiSnap) => void;
}) {
  const drag = useRef<{ y: number; h: number; moved: boolean } | null>(null);
  /** Set when a drag ends, so the click the browser sends after it is not a second toggle. */
  const dragged = useRef(false);
  const settle = () => {
    if (sheet.current) sheet.current.style.height = "";
  };
  return {
    onPointerDown(e: PointerEvent<HTMLButtonElement>) {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      drag.current = { y: e.clientY, h: sheet.current?.offsetHeight ?? 0, moved: false };
    },
    onPointerMove(e: PointerEvent<HTMLButtonElement>) {
      const d = drag.current;
      if (!d) return;
      const dy = e.clientY - d.y;
      if (Math.abs(dy) > 4) d.moved = true;
      // Follows the finger between the two heights; the stylesheet's max-height still caps it.
      if (sheet.current && d.moved && d.h > 0)
        sheet.current.style.height = `${Math.max(0, d.h - dy)}px`;
    },
    onPointerUp(e: PointerEvent<HTMLButtonElement>) {
      const d = drag.current;
      drag.current = null;
      settle();
      if (!d || !d.moved) return;
      dragged.current = true;
      const dy = e.clientY - d.y;
      if (dy <= -DRAG_PX) onSnap("full");
      else if (dy >= DRAG_PX) onSnap("card");
    },
    onPointerCancel() {
      drag.current = null;
      settle();
    },
    onClick() {
      if (dragged.current) {
        dragged.current = false;
        return;
      }
      onSnap(open ? "card" : "full");
    },
  };
}
